import type { Breakpoint, CSSObject } from '@mui/system';
import type { CSSProperties } from 'react';
import theme from 'src/components-v2/theme';

/**
 * A font style defines all typographical properties and corresponds to CSS
 * font-family,font-size,font-weight,letter-spacing,and line-height.
 *
 * Values reflect the Refreshed Web Type Stack (Dec 2025).
 *
 * @see [redacted]##################################################################################
 */
export type TDesignSystemFontStyle =
  | 'Display Headline'
  | 'XL Headline'
  | 'Large Headline'
  | 'Medium Headline'
  | 'Medium Headline Serif'
  | 'Small Headline'
  | 'Subhead SemiBold'
  | 'Subhead Regular'
  | 'Eyebrow'
  | 'P1 SemiBold'
  | 'P1 Regular'
  | 'P2 SemiBold'
  | 'P2 Regular'
  | 'L Stat'
  | 'M Stat'
  | 'S Stat'
  | 'Disclosure';

/**
 * Pre-Dec-2025 style names that published CMS entries (decorativeText
 * fontPreset/fontSize) may still hold until the rename migration
 * (migrations/*-rename-decorative-text-font-styles.js) runs against master.
 * Normalize CMS values with resolveDesignSystemFontStyle.
 *
 * Delete the map, the type, and the resolver only after the migration has run
 * everywhere AND a Delivery API query confirms no decorativeText entry still
 * holds a legacy value — the migration cannot republish entries with pending
 * draft edits and never touches archived entries, so those can retain legacy
 * names in their published/stored content after it runs.
 */
export const LegacyDesignSystemFontStyleMap = {
  'XL Header': 'XL Headline',
  'Lrg Header': 'Large Headline',
  'Med Header': 'Medium Headline',
  'Med Header Serif': 'Medium Headline Serif',
  'Sm Header': 'Small Headline',
} satisfies Record<string, TDesignSystemFontStyle>;

export type TLegacyDesignSystemFontStyle =
  keyof typeof LegacyDesignSystemFontStyleMap;

const isLegacyDesignSystemFontStyle = (
  fontStyle: string,
): fontStyle is TLegacyDesignSystemFontStyle => {
  return Object.prototype.hasOwnProperty.call(
    LegacyDesignSystemFontStyleMap,
    fontStyle,
  );
};

export function resolveDesignSystemFontStyle(
  fontStyle: TDesignSystemFontStyle | TLegacyDesignSystemFontStyle,
): TDesignSystemFontStyle {
  return isLegacyDesignSystemFontStyle(fontStyle)
    ? LegacyDesignSystemFontStyleMap[fontStyle]
    : fontStyle;
}

/** CSS font-weight */
export type TDesignSystemFontWeight =
  | 'Regular'
  | 'SemiBold'
  | 'ExtraBold'
  | 'Light';

/** CSS font-family */
export type TDesignSystemFontFamily =
  | 'Plaid Sans'
  | 'Cern'
  | 'Inconsolata'
  | 'Bowery Street';

export type TDesignSystemFontStyleRegistryEntry = {
  fontFamily: TDesignSystemFontFamily;
  fontSize: string;
  fontWeight: TDesignSystemFontWeight;
} & Partial<
  Pick<CSSProperties, 'textTransform' | 'letterSpacing' | 'lineHeight'>
>;

export const DesignSystemFontWeightMap: Record<
  TDesignSystemFontWeight,
  number
> = {
  Regular: 400,
  SemiBold: 600,
  ExtraBold: 800,
  Light: 300,
};

export const DesignSystemFontFamilyMap: Record<
  TDesignSystemFontFamily,
  string
> = {
  // Sourced from Threads5
  // @see [redacted]########################################################################################################################
  'Bowery Street': '"Bowery Street",serif',
  'Plaid Sans': `"Plaid Sans",Cern,"Avenir Next",Avenir,"Proxima Nova","Jakarta Plus Sans",Futura,"Avant Garde",Montserrat,"Helvetica Neue",Helvetica,"Nimbus Sans",system-ui,-apple-system,BlinkMacSystemFont,Arial,"Segoe UI",Roboto,Oxygen,sans-serif`,
  Cern: 'Cern,"Helvetica Neue",Helvetica,"Nimbus Sans",system-ui,-apple-system,BlinkMacSystemFont,Arial,"Segoe UI",Roboto,Oxygen,sans-serif',
  Inconsolata: `Inconsolata,ui-monospace,Menlo,Monaco,"Cascadia Mono","Segoe UI Mono","Roboto Mono","Oxygen Mono","Ubuntu Mono","Source Code Pro","Fira Mono","Droid Sans Mono","Consolas","Courier New",Courier,monospace`,
};

export const DesignSystemFontStyleRegistry: Record<
  TDesignSystemFontStyle,
  {
    [key in Breakpoint]?: TDesignSystemFontStyleRegistryEntry;
  } & {
    [key in Extract<
      Breakpoint,
      'md' | 'xs'
    >]: TDesignSystemFontStyleRegistryEntry;
  }
> = {
  'Display Headline': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '7.6rem',
      lineHeight: '8rem',
      letterSpacing: '-0.2rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '3.8rem',
      lineHeight: '3.6rem',
      letterSpacing: '-0.15rem',
    },
  },
  'XL Headline': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '6.4rem',
      lineHeight: '6.8rem',
      letterSpacing: '-0.2rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '3.2rem',
      lineHeight: '3.6rem',
      letterSpacing: '-0.15rem',
    },
  },
  'Large Headline': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '4.8rem',
      lineHeight: '5.6rem',
      letterSpacing: '-0.2rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '2.4rem',
      lineHeight: '3rem',
      letterSpacing: '-0.1rem',
    },
  },
  'Medium Headline': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '3.6rem',
      lineHeight: '4.2rem',
      letterSpacing: '-0.2rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '2.2rem',
      lineHeight: '2.8rem',
      letterSpacing: '-0.1rem',
    },
  },
  // Not in the Dec 2025 Figma stack; kept because CMS entries still use it
  // (see RetiredDesignSystemFontStyles).
  'Medium Headline Serif': {
    md: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      fontSize: '4rem',
      lineHeight: '160%',
      letterSpacing: '-0.04rem',
    },
    xs: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      fontSize: '3rem',
      lineHeight: '150%',
      letterSpacing: '-0.03rem',
    },
  },
  'Small Headline': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '2.8rem',
      lineHeight: '3.6rem',
      letterSpacing: '-0.15rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '2rem',
      lineHeight: '2.6rem',
      letterSpacing: '-0.1rem',
    },
  },
  // Intentionally Plaid Sans, not the Cern in the linked Figma table — that
  // cell is stale (superseded by SITE-6128).
  'Subhead SemiBold': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '2.4rem',
      lineHeight: '3.6rem',
      letterSpacing: '-0.08rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'SemiBold',
      fontSize: '2rem',
      lineHeight: '2.8rem',
      letterSpacing: '-0.05rem',
    },
  },
  'Subhead Regular': {
    md: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'Regular',
      fontSize: '2.4rem',
      lineHeight: '3.6rem',
      letterSpacing: '-0.08rem',
    },
    xs: {
      fontFamily: 'Plaid Sans',
      fontWeight: 'Regular',
      fontSize: '2rem',
      lineHeight: '2.8rem',
      letterSpacing: '-0.05rem',
    },
  },
  Eyebrow: {
    md: {
      fontFamily: 'Cern',
      fontWeight: 'ExtraBold',
      fontSize: '1.6rem',
      lineHeight: '2.6rem',
      letterSpacing: '0.3rem',
      textTransform: 'uppercase',
    },
    xs: {
      fontFamily: 'Cern',
      fontWeight: 'ExtraBold',
      fontSize: '1.4rem',
      lineHeight: '2.2rem',
      letterSpacing: '0.2rem',
      textTransform: 'uppercase',
    },
  },
  'P1 SemiBold': {
    md: {
      fontFamily: 'Cern',
      fontWeight: 'SemiBold',
      fontSize: '2rem',
      lineHeight: '3rem',
      letterSpacing: '-0.02rem',
    },
    xs: {
      fontFamily: 'Cern',
      fontWeight: 'SemiBold',
      fontSize: '1.8rem',
      lineHeight: '2.6rem',
      letterSpacing: '-0.02rem',
    },
  },
  'P1 Regular': {
    md: {
      fontFamily: 'Cern',
      fontWeight: 'Regular',
      fontSize: '2rem',
      lineHeight: '3rem',
      letterSpacing: '-0.02rem',
    },
    xs: {
      fontFamily: 'Cern',
      fontWeight: 'Regular',
      fontSize: '1.8rem',
      lineHeight: '2.6rem',
      letterSpacing: '-0.02rem',
    },
  },
  'P2 SemiBold': {
    md: {
      fontFamily: 'Cern',
      fontWeight: 'SemiBold',
      fontSize: '1.8rem',
      lineHeight: '2.8rem',
      letterSpacing: '-0.03rem',
    },
    xs: {
      fontFamily: 'Cern',
      fontWeight: 'SemiBold',
      fontSize: '1.6rem',
      lineHeight: '2.4rem',
      letterSpacing: '-0.02rem',
    },
  },
  'P2 Regular': {
    md: {
      fontFamily: 'Cern',
      fontWeight: 'Regular',
      fontSize: '1.8rem',
      lineHeight: '2.8rem',
      letterSpacing: '-0.03rem',
    },
    xs: {
      fontFamily: 'Cern',
      fontWeight: 'Regular',
      fontSize: '1.6rem',
      lineHeight: '2.4rem',
      letterSpacing: '-0.02rem',
    },
  },
  'L Stat': {
    md: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      // Fluid below 1200px so long stats don't overflow narrow desktop
      // columns; caps at the spec's 120px
      fontSize: 'min(10vw, 12rem)',
      lineHeight: '88%',
      letterSpacing: '-0.25rem',
    },
    xs: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      fontSize: '4.6rem',
      lineHeight: '3.6rem',
      letterSpacing: '-0.15rem',
    },
  },
  'M Stat': {
    md: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      fontSize: '8rem',
      lineHeight: '7.2rem',
      letterSpacing: '-0.25rem',
    },
    xs: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      fontSize: '3.6rem',
      lineHeight: '3.6rem',
      letterSpacing: 'normal',
    },
  },
  'S Stat': {
    md: {
      fontSize: '6.4rem',
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      lineHeight: '5.6rem',
      letterSpacing: '-0.25rem',
    },
    xs: {
      fontFamily: 'Bowery Street',
      fontWeight: 'Light',
      fontSize: '3.2rem',
      lineHeight: '3.6rem',
      letterSpacing: 'normal',
    },
  },
  Disclosure: {
    md: {
      fontFamily: 'Cern',
      fontWeight: 'Regular',
      fontSize: '1.4rem',
      lineHeight: '2rem',
      letterSpacing: 'normal',
    },
    xs: {
      fontFamily: 'Cern',
      fontWeight: 'Regular',
      fontSize: '1.4rem',
      lineHeight: '1.5rem',
      letterSpacing: 'normal',
    },
  },
};

/**
 * Styles the Dec 2025 type stack dropped. They stay renderable for published
 * CMS content.
 */
export const RetiredDesignSystemFontStyles: ReadonlySet<TDesignSystemFontStyle> =
  new Set(['Medium Headline Serif']);

export function getCssFontStyle(
  fontStyle: TDesignSystemFontStyle,
  breakpoint: Breakpoint,
): CSSProperties | null {
  // CMS-sourced names can drift from the registry (e.g. an option added in
  // Contentful before the code catches up); warn and degrade to no styles
  // rather than throwing during render/static export.
  const registryEntry = DesignSystemFontStyleRegistry[fontStyle];

  if (!registryEntry) {
    // eslint-disable-next-line no-console -- surface content drift in static-export build logs
    console.warn(
      `Unknown design-system font style "${fontStyle}"; rendering unstyled`,
    );
    return null;
  }

  // A style defines only some breakpoints (md/xs required, rest optional);
  // a missing breakpoint is expected and not worth a warning.
  const entry = registryEntry[breakpoint];

  if (!entry) {
    return null;
  }

  return {
    ...entry,
    fontFamily: DesignSystemFontFamilyMap[entry.fontFamily],
    fontWeight: DesignSystemFontWeightMap[entry.fontWeight],
  };
}

// Reference `typeof theme` as a discrete type instead of inline to prevent a `@typescript-eslint/no-unused-vars` error
type Theme = typeof theme;

export function getResponsiveCssFontStyles<TTheme extends Theme>(
  theme: TTheme,
  fontStyle: TDesignSystemFontStyle,
): CSSObject {
  const styles: CSSObject = {};

  theme.breakpoints.keys.forEach((breakpoint) => {
    const cssFontStyle = getCssFontStyle(fontStyle, breakpoint);
    if (!cssFontStyle) {
      return;
    }

    styles[theme.breakpoints.up(breakpoint)] = cssFontStyle;
  });

  return styles;
}
